import { Box } from "@chakra-ui/react";
import { format as formatDate } from "date-fns";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { DatePickerGlobalStyles } from "./DatePickerGlobalStyles";
import { DatePickerHeader } from "./DatePickerHeader";
import {
  PORTAL_ID,
  PickerCustomInput,
  datepickerLocaleMap,
  parsePickerIsoDate,
  useDatepickerPortal,
} from "./pickerInternals";

interface ThemedDatePickerProps {
  id?: string;
  defaultDate?: string;
  date?: string | null;
  onChange: (date?: string) => Promise<void> | void;
  onBlur?: () => Promise<void> | void;
  isDisabled?: boolean;
  autoFocus?: boolean;
  placeholder?: string;
  allowPastDates?: boolean;
  minDate?: Date;
}

export const ThemedDatePicker = ({
  id,
  defaultDate,
  date,
  onChange,
  onBlur,
  isDisabled,
  autoFocus = false,
  placeholder,
  allowPastDates,
  minDate,
}: ThemedDatePickerProps) => {
  const { i18n } = useTranslation();
  const intlLocale = datepickerLocaleMap[i18n.language] ?? "nb-NO";
  const [internalDate, setInternalDate] = useState<Date | null>(parsePickerIsoDate(defaultDate));
  const selected = date !== undefined ? parsePickerIsoDate(date) : internalDate;

  useDatepickerPortal();

  const handleChange = (d: Date | null) => {
    setInternalDate(d);
    onChange(d ? formatDate(d, "yyyy-MM-dd") : undefined);
  };

  return (
    <Box sx={{ ".react-datepicker-wrapper": { maxWidth: { sm: "full", md: "60" } } }}>
      <DatePickerGlobalStyles />
      <DatePicker
        id={id}
        selected={selected}
        onChange={handleChange}
        onCalendarClose={onBlur}
        autoFocus={autoFocus}
        dateFormat="dd.MM.yyyy"
        placeholderText={placeholder ?? "dd.mm.yyyy"}
        customInput={<PickerCustomInput />}
        disabled={isDisabled}
        minDate={allowPastDates ? undefined : (minDate ?? new Date())}
        showPopperArrow={false}
        popperPlacement="bottom-start"
        portalId={PORTAL_ID}
        renderCustomHeader={(props) => <DatePickerHeader {...props} locale={intlLocale} />}
      />
    </Box>
  );
};
